/**
 * StandaloneSummativeBuilder — Phase 119.C · Feb 2026.
 *
 * Routes:
 *   /assess/build/performance-task
 *   /assess/build/project
 *
 * Sets up a standalone Performance Task or Project (the only
 * difference is the `project_mode` flag passed to the backend, which
 * reframes the prompt as multi-period planning). Reuses the shared
 * LessonGroundingPicker and writes a Gradebook row server-side after
 * the activity is generated.
 *
 * Multi-expectation requirement (backend-enforced for summative_task):
 * the teacher must pick at least 2 expectation codes before generate.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FlaskConical, FileText, Loader2, Sparkles } from "lucide-react";
import AppShell from "../components/shell/AppShell";
import { useAuth } from "../contexts/AuthContext";
import {
  useCurriculum,
  getGradesFromCurriculum,
  getSubjectsForGrade,
  getStrandsForGradeSubject,
  getSpecificsForGradeSubject,
} from "../lib/curriculum";
import LessonGroundingPicker from "../components/LessonGroundingPicker";
import { generateSummativeTask, listFolders, autoFileActivity, getActivity } from "../lib/api";
import SaveToFolderModal from "../components/SaveToFolderModal";

const MIN_EXPECTATIONS = 2;
const DEFAULT_NUM_LESSONS = 3;

export default function StandaloneSummativeBuilder() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  // Phase 122 — Save-to-folder modal state.
  const [savedStub, setSavedStub] = useState(null);
  const [folders, setFolders] = useState([]);
  const location = useLocation();
  const isProject = location.pathname.endsWith("/project");
  const kindLabel = isProject ? "Project" : "Performance Task";
  const Icon = isProject ? FileText : FlaskConical;

  const { data: curriculum } = useCurriculum();
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [strand, setStrand] = useState("");
  const [expectations, setExpectations] = useState([]);
  const [time, setTime] = useState("60");
  const [numLessons, setNumLessons] = useState(isProject ? 4 : 2);
  const [activityType, setActivityType] = useState("");
  const [reportingPeriod, setReportingPeriod] = useState("term_1");
  const [attachedLessonIds, setAttachedLessonIds] = useState([]);
  // Phase 128 — When ON, the LLM emits Support + On-Grade + Extension
  // versions of every student sheet. Default OFF → on-grade only.
  const [includeDifferentiatedSheets, setIncludeDifferentiatedSheets] = useState(false);
  const [busy, setBusy] = useState(false);

  const gradeOptions = curriculum ? getGradesFromCurriculum(curriculum) : [];
  const subjectOptions = grade && curriculum ? getSubjectsForGrade(curriculum, grade) : [];
  const strandOptions = grade && subject && curriculum ? getStrandsForGradeSubject(curriculum, grade, subject) : [];
  const expectationOptions = useMemo(() => {
    if (!grade || !subject || !curriculum) return [];
    const all = getSpecificsForGradeSubject(curriculum, grade, subject);
    if (!strand) return all;
    const strandCode = (strand.split(/[\s—–-]/)[0] || strand).trim().toUpperCase();
    return all.filter((e) => (e.code || "").toUpperCase().startsWith(strandCode));
  }, [curriculum, grade, subject, strand]);

  if (!authLoading && !user) return <Navigate to="/" replace />;

  const toggleExpectation = (code) => {
    setExpectations((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      return [...prev, code];
    });
  };

  const canGenerate = grade && subject && expectations.length >= MIN_EXPECTATIONS && numLessons >= 2 && !busy;

  const handleGenerate = async () => {
    if (!canGenerate) {
      if (expectations.length < MIN_EXPECTATIONS) {
        toast.error(`Pick at least ${MIN_EXPECTATIONS} expectations.`);
      }
      return;
    }
    setBusy(true);
    try {
      const payload = {
        output_type: "summative_task",
        grade,
        subject,
        strand,
        expectations,
        num_lessons: Number(numLessons) || DEFAULT_NUM_LESSONS,
        time,
        activity_type: activityType,
        attached_lesson_ids: attachedLessonIds,
        project_mode: isProject,
        reporting_period: reportingPeriod,
        // Phase 128 — differentiation toggle (default off → on-grade only).
        include_differentiated_sheets: includeDifferentiatedSheets,
      };
      const activity = await generateSummativeTask(payload);
      // Phase 121/122 — apply auto-file then prompt the teacher to
      // confirm/override the destination folder. The Save-to-folder
      // modal shows up before we navigate to the lesson workspace.
      try {
        await autoFileActivity(activity.id);
        const [refreshed, fs] = await Promise.all([
          getActivity(activity.id),
          listFolders(),
        ]);
        setFolders(fs || []);
        setSavedStub(refreshed);
      } catch (_err) {
        // Auto-file is best-effort. If anything goes wrong, fall
        // through to the legacy navigate (no modal shown).
        toast.success(`${kindLabel} generated.`);
        navigate(`/lesson/${activity.id}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Generation failed";
      toast.error(`Couldn't generate the ${kindLabel.toLowerCase()}`, { description: String(detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title={`Build a ${kindLabel}`} subtitle={`Standalone ${kindLabel.toLowerCase()}`}>
      <div className="bg-[#f4f5f7] min-h-full">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          <Link
            to="/assess/build"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#4a6984] hover:text-[#1c2d3d]"
            data-testid="standalone-summative-back"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to Build a Summative
          </Link>

          <header className="flex items-start gap-3">
            <span className="rounded-md bg-[#96ded1]/30 p-2.5 text-[#1c2d3d]">
              <Icon className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-black text-2xl text-[#1c2d3d]">
                Build a standalone {kindLabel.toLowerCase()}
              </h2>
              <p className="mt-1 text-sm text-[#4a6984] max-w-2xl leading-snug">
                {isProject
                  ? "A multi-period project spanning multiple Ontario expectations. Pace it across 2-10 classes."
                  : "A culminating performance task that students complete in one or two classes to demonstrate learning across 2+ expectations."}
              </p>
            </div>
          </header>

          <section className="ae-card p-6 space-y-4" data-testid="standalone-summative-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Grade *">
                <select
                  value={grade}
                  onChange={(e) => { setGrade(e.target.value); setSubject(""); setStrand(""); setExpectations([]); }}
                  className="ae-form-control"
                  data-testid="standalone-summative-grade"
                >
                  <option value="">Pick…</option>
                  {gradeOptions.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Subject *">
                <select
                  value={subject}
                  onChange={(e) => { setSubject(e.target.value); setStrand(""); setExpectations([]); }}
                  disabled={!grade}
                  className="ae-form-control disabled:opacity-40"
                  data-testid="standalone-summative-subject"
                >
                  <option value="">Pick…</option>
                  {subjectOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Strand (optional)">
                <select
                  value={strand}
                  onChange={(e) => { setStrand(e.target.value); setExpectations([]); }}
                  disabled={!subject}
                  className="ae-form-control disabled:opacity-40"
                  data-testid="standalone-summative-strand"
                >
                  <option value="">All strands</option>
                  {strandOptions.map((s) => {
                    const label = typeof s === "string" ? s : `${s.code} — ${s.name}`;
                    const key = typeof s === "string" ? s : s.code;
                    return <option key={key} value={label}>{label}</option>;
                  })}
                </select>
              </Field>
              <Field label={isProject ? "Number of classes (2–10) *" : "Number of class periods (2–10) *"}>
                <input
                  type="number"
                  min={2}
                  max={10}
                  value={numLessons}
                  onChange={(e) => setNumLessons(e.target.value)}
                  className="ae-form-control"
                  data-testid="standalone-summative-num-lessons"
                />
              </Field>
              <Field label="Time per class (mins)">
                <input
                  type="text"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="ae-form-control"
                  placeholder="e.g. 60"
                  data-testid="standalone-summative-time"
                />
              </Field>
              <Field label="Reporting period">
                <select
                  value={reportingPeriod}
                  onChange={(e) => setReportingPeriod(e.target.value)}
                  className="ae-form-control"
                  data-testid="standalone-summative-reporting-period"
                >
                  <option value="progress_report">Progress Report (Fall)</option>
                  <option value="term_1">Term 1 (Winter)</option>
                  <option value="term_2">Term 2 (Spring)</option>
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Task format hint (optional)">
                  <input
                    type="text"
                    value={activityType}
                    onChange={(e) => setActivityType(e.target.value)}
                    placeholder={isProject ? "e.g. Documentary film, Design challenge, Inquiry portfolio" : "e.g. Lab demo, Persuasive speech, Maker challenge"}
                    className="ae-form-control"
                    data-testid="standalone-summative-activity-type"
                  />
                </Field>
              </div>
            </div>
          </section>

          <section className="ae-card p-6" data-testid="standalone-summative-expectations">
            <header className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#4a6984]/70">
                  Expectations *
                </p>
                <p className="text-xs text-[#4a6984]/85">
                  Pick at least {MIN_EXPECTATIONS} expectations this {kindLabel.toLowerCase()} should cover.
                </p>
              </div>
              <span className="ae-pill bg-[#96ded1]/30 text-[#1c2d3d]" data-testid="standalone-summative-expectation-count">
                {expectations.length} selected
              </span>
            </header>
            <div className="mt-3 rounded-md border border-[#4a6984]/15 bg-white max-h-64 overflow-y-auto">
              {!subject ? (
                <p className="p-4 text-xs italic text-[#4a6984]/75">Pick a grade and subject first.</p>
              ) : expectationOptions.length === 0 ? (
                <p className="p-4 text-xs italic text-[#4a6984]/75">No expectations indexed for this subject yet.</p>
              ) : (
                <ul className="divide-y divide-[#4a6984]/10">
                  {expectationOptions.map((e) => {
                    const sel = expectations.includes(e.code);
                    return (
                      <li key={e.code}>
                        <button
                          type="button"
                          onClick={() => toggleExpectation(e.code)}
                          aria-pressed={sel}
                          data-testid={`standalone-summative-expectation-${e.code}`}
                          className={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-[#f4f5f7] ${sel ? "bg-[#96ded1]/15" : ""}`}
                        >
                          <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 text-[9px] font-bold ${sel ? "border-[#1c9e7e] bg-[#1c9e7e] text-white" : "border-[#4a6984]/40 bg-white"}`}>
                            {sel ? "✓" : ""}
                          </span>
                          <span className="flex-1">
                            <span className="font-bold text-xs text-[#1c2d3d]">{e.code}</span>
                            <span className="text-xs text-[#4a6984]"> — {e.description}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          <section className="ae-card p-6">
            <LessonGroundingPicker
              selectedIds={attachedLessonIds}
              onChange={setAttachedLessonIds}
              subjects={subject ? [subject] : []}
              grade={grade}
              emptyHint={subject ? "No taught lessons in this subject yet." : "Pick a subject first."}
            />
          </section>

          {/* Phase 128 — Differentiation toggle. Off (default) → on-grade
              sheets only. On → also generates Support + Extension. */}
          <section
            className="ae-card p-6"
            data-testid="standalone-summative-differentiation"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeDifferentiatedSheets}
                onChange={(e) => setIncludeDifferentiatedSheets(e.target.checked)}
                data-testid="standalone-summative-differentiation-toggle"
                className="mt-1 h-4 w-4 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
              />
              <span className="min-w-0">
                <span className="block font-extrabold text-sm text-[#1c2d3d] leading-tight">
                  Include differentiated versions of student sheets
                </span>
                <span className="block text-xs text-[#4a6984]/80 mt-1 leading-relaxed">
                  Off (default) — on-grade sheets only. On — also generates
                  a Support version (heavier scaffolding, sentence frames)
                  and an Extension version (fewer scaffolds, more depth).
                  The rubric stays the same across versions.
                </span>
              </span>
            </label>
          </section>

          <div className="flex items-center justify-between sticky bottom-0 bg-[#f4f5f7]/95 backdrop-blur py-3">
            <p className="text-xs text-[#4a6984]/80">
              {expectations.length < MIN_EXPECTATIONS
                ? `Pick at least ${MIN_EXPECTATIONS - expectations.length} more expectation${MIN_EXPECTATIONS - expectations.length === 1 ? "" : "s"} to enable Generate.`
                : "Ready to generate."}
            </p>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-[#1c2d3d] text-white font-bold text-sm hover:bg-[#0e1a25] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="standalone-summative-generate-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {busy ? "Generating…" : `Generate ${kindLabel}`}
            </button>
          </div>
        </main>
      </div>
      <SaveToFolderModal
        open={!!savedStub}
        activity={savedStub}
        folders={folders}
        onClose={() => {
          // Either way the teacher accepted the placement — navigate
          // them to the lesson workspace so they can refine the body.
          const id = savedStub?.id;
          setSavedStub(null);
          if (id) navigate(`/lesson/${id}`);
        }}
        onMoved={() => {/* handled in onClose */}}
      />
    </AppShell>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/75 mb-1">{label}</span>
    {children}
  </label>
);
